import { ChangeDetectionStrategy, Component, Input, signal, inject, Output, EventEmitter } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { map } from 'rxjs/operators';
import { CodingSystemsHttpService } from 'shared/domain';
import {
  ConfirmationDialogComponent,
  DialogService,
  FormFieldComponent,
  FormFieldImports,
  IconDirective,
  InputDirective,
  LabelDirective,
  MenuComponent,
  MenuItemDirective,
  MenuTriggerDirective,
  SelectImports,
  TooltipDirective,
  ButtonDirective,
  ConfirmationDialogArgs,
} from 'shared/ui-common';
import { isTruthy, SharedUtilCommonModule } from 'shared/util-common';

import { CodingValue, CodingSystemDto } from '@treetop/shared/model';

type EditableCoding = {
  id: string;
} & Partial<CodingValue>;

@Component({
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'tt-coding-systems-selector',
  templateUrl: './coding-systems-selector.component.html',
  styleUrls: ['./coding-systems-selector.component.scss'],
  imports: [
    SharedUtilCommonModule,
    IconDirective,
    TooltipDirective,
    FormFieldComponent,
    InputDirective,
    LabelDirective,

    SelectImports,
    FormsModule,
    FormFieldImports,
    MenuComponent,
    MenuItemDirective,
    MenuTriggerDirective,
    ButtonDirective,
  ],
})
export class CodingSystemsSelectorComponent {
  private codingSystemsHttpService = inject(CodingSystemsHttpService);
  private dialogService = inject(DialogService);
  private translocoService = inject(TranslocoService);

  protected codingSystemsMap = toSignal(
    this.codingSystemsHttpService.get().pipe(
      map((codingSystems) => {
        const codingSystemsMap = new Map<string, CodingSystemDto>();
        codingSystems.forEach((codingSystem) => codingSystemsMap.set(codingSystem.id, codingSystem));
        return codingSystemsMap;
      }),
    ),
  );

  protected _codings = signal<EditableCoding[]>([]);
  @Input() public set codings(codings: CodingValue[]) {
    this._codings.set(
      (codings ?? []).map((coding) => ({
        ...coding,
        id: `${coding.systemId}-${coding.versionId}-${coding.code}`,
      })),
    );
  }

  @Output() public codingsChanged = new EventEmitter<CodingValue[]>();

  protected currentlyEditing = signal<EditableCoding | null>(null);

  protected handleCodingEdited() {
    const codings = this._codings()
      .filter((coding) => coding.id)
      .map(({ id, ...rest }) => rest as CodingValue);

    this.codingsChanged.emit(codings);
    this.currentlyEditing.set(null);
  }

  protected handleRemoveCoding(coding: EditableCoding) {
    const data: ConfirmationDialogArgs = {
      message: 'RemoveCoding.Message',
      params: {
        code: coding.code,
      },
      cancelButtonCaption: 'Cancel',
      confirmButtonCaption: 'Delete',
    };

    this.dialogService
      .open(ConfirmationDialogComponent, {
        title: 'RemoveCoding.Title',
        data,
      })
      .afterClosed()
      .pipe(isTruthy())
      .subscribe(() => {
        this._codings.update((codings) => codings.filter((existingCoding) => existingCoding.id !== coding.id));

        this.codingsChanged.emit(this._codings());
      });
  }

  protected handleCancel() {
    this.currentlyEditing.set(null);
    this._codings.update((codings) => codings.filter((coding) => coding.id));
  }

  protected handleCodingAdded() {
    const newCoding = {
      systemId: this.currentlyEditing().systemId,
      versionId: this.currentlyEditing().versionId,
      code: this.currentlyEditing().code,
    };

    const codings = this._codings()
      .filter((coding) => coding.id)
      .map(({ id, ...rest }) => rest as CodingValue);
    codings.push(newCoding);

    this.codingsChanged.emit(codings);
    this.currentlyEditing.set(null);
  }

  protected createCoding() {
    const newCoding: EditableCoding = {
      id: '',
      systemId: '',
      versionId: undefined,
      code: '',
    };
    this._codings.update((codings) => [...codings, newCoding]);
    this.currentlyEditing.set(newCoding);
  }

  protected handleSystemChanged() {
    this.currentlyEditing.update((coding) => ({
      ...coding,
      versionId: undefined,
    }));
  }
}
